import { useAtomValue, useSetAtom } from 'jotai'
import { memo, useCallback, useEffect } from 'react'
import { twMerge } from 'tailwind-merge'
import { useIsWindowFocused } from '../../../../hooks'
import { Popover, PopoverContent, PopoverTrigger } from '../../../radix/popover'
import { closeMentionsAtom, mentionsSearchFieldAtom, useIsShowingMentions } from './atoms'
import { MentionsCombobox } from './nested/mentions-combobox'
import { MentionsMenu } from './nested/mentions-menu'

// The menu's public surface — the popover root below, plus the hooks surfaces
// outside `input/` drive it with (the sidepanel hotkeys, the assistant event
// handler, submit). Everything else in this folder is an internal of the menu.
export {
  useCloseMentions,
  useIsShowingMentions,
  useOpenMentions,
  useMessageMentions,
  useSetMessageMentions,
} from './atoms'
export { escapeRegex, useResetMentions, useValidateMentions } from './hooks/use-mentions-handler'
// The menu's look and a skill's mention, for the sidebar search bar's own `/` list.
export { SKILLS_PAGE_LINK, toSkillMention } from './hooks/use-filtered-mention-data'
export { MentionableRow } from './nested/mentionable-row'
export { MentionSectionHeading, mentionsPopupClassName } from './nested/mention-list-parts'

export const MentionsMenuPopover = memo(
  ({
    children,
    className,
    sideOffset,
    alignOffset,
  }: React.PropsWithChildren<{ className?: string; sideOffset?: number; alignOffset?: number }>) => {
    const isShowingMentions = useIsShowingMentions()
    const closeMentions = useSetAtom(closeMentionsAtom)
    const isWindowFocused = useIsWindowFocused()
    const hasSearchField = useAtomValue(mentionsSearchFieldAtom)

    const onFocusPrevention = useCallback((e: Event) => {
      e.preventDefault()
    }, [])

    const onClickTrigger = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault()
        if (isShowingMentions) {
          closeMentions()
        }
      },
      [isShowingMentions, closeMentions],
    )

    const onOpenChange = useCallback(
      (open: boolean) => {
        if (!open) {
          closeMentions()
        }
      },
      [closeMentions],
    )

    useEffect(() => {
      if (isShowingMentions && !isWindowFocused) {
        closeMentions()
      }
    }, [isWindowFocused, isShowingMentions, closeMentions])

    // The composer can go out from under an open menu without any dismissal
    // running — the login prompt replacing it, a view swap. The atoms outlive
    // the tree, so close on the way out or the next mount re-opens the menu
    // mid-session, still carrying the old query and mode.
    useEffect(() => closeMentions, [closeMentions])

    return (
      <Popover open={isShowingMentions} onOpenChange={onOpenChange}>
        <PopoverTrigger
          className={twMerge('m-0 bg-transparent p-0 outline-none', className)}
          asChild={true}
          onClick={onClickTrigger}
        >
          {children}
        </PopoverTrigger>
        <PopoverContent
          side={'top'}
          sideOffset={sideOffset}
          align={'start'}
          alignOffset={alignOffset}
          className={'m-0 h-fit w-fit rounded-none border-none bg-transparent p-0 shadow-none'}
          onOpenAutoFocus={onFocusPrevention}
          onCloseAutoFocus={onFocusPrevention}
        >
          {hasSearchField ? <MentionsCombobox /> : <MentionsMenu />}
        </PopoverContent>
      </Popover>
    )
  },
)
MentionsMenuPopover.displayName = 'MentionsMenuPopover'
